import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { EVMData } from '@/types/evm';
import { generateSCurveData } from '@/utils/advanced-evm-calculations';
import { CHART_COLORS } from '@/lib/constants';
import { CHART_CONFIG } from '@/components/Charts/config';

interface SCurveChartProps {
  data: EVMData;
  height?: number;
  showGrid?: boolean;
  periodCount?: number;
}

/**
 * S-Curve chart showing Planned Value, Earned Value, and Actual Cost over time.
 * The S-Curve is a characteristic visualization in project management showing
 * cumulative values following a sigmoid distribution.
 */
export function SCurveChart({
  data,
  height = 350,
  showGrid = true,
  periodCount = 12,
}: SCurveChartProps) {
  const chartData = generateSCurveData(data, periodCount);

  // If no data available, show a message
  if (chartData.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <p>Enter project dates and BAC to view S-Curve</p>
      </div>
    );
  }

  // Find current period index for reference line
  const now = new Date();
  const currentPeriodIndex = chartData.findIndex(
    (point, index) =>
      index === chartData.length - 1 ||
      (point.date <= now && chartData[index + 1]?.date > now)
  );

  // Format currency for tooltips and axis
  const formatCurrency = (value: number) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toFixed(0);
  };

  // Custom tooltip formatter
  const tooltipFormatter = (value: number, name: string) => {
    const labels: Record<string, string> = {
      plannedValue: 'Planned Value (PV)',
      earnedValue: 'Earned Value (EV)',
      actualCost: 'Actual Cost (AC)',
    };
    return [
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(value),
      labels[name] || name,
    ];
  };

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={CHART_CONFIG.defaults.margin}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray={CHART_CONFIG.defaults.gridDasharray}
              className="stroke-muted"
            />
          )}

          <XAxis
            dataKey="period"
            tickFormatter={(value) => `P${value}`}
            tick={{ fontSize: 12 }}
            className="text-muted-foreground"
          />

          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12 }}
            className="text-muted-foreground"
            width={60}
          />

          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={CHART_CONFIG.tooltip.contentStyle}
            labelFormatter={(label) => `Period ${label}`}
          />

          <Legend
            verticalAlign="top"
            height={36}
            formatter={(value) => {
              const labels: Record<string, string> = {
                plannedValue: 'Planned Value (PV)',
                earnedValue: 'Earned Value (EV)',
                actualCost: 'Actual Cost (AC)',
              };
              return labels[value] || value;
            }}
          />

          {/* Current period reference line */}
          {currentPeriodIndex >= 0 && (
            <ReferenceLine
              x={currentPeriodIndex}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              label={{
                value: 'Today',
                position: 'top',
                fontSize: 11,
                fill: 'hsl(var(--muted-foreground))',
              }}
            />
          )}

          {/* BAC reference line */}
          {data.bac && (
            <ReferenceLine
              y={data.bac}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="3 3"
              label={{
                value: 'BAC',
                position: 'right',
                fontSize: 11,
                fill: 'hsl(var(--muted-foreground))',
              }}
            />
          )}

          {/* Planned Value - Primary line */}
          <Line
            type="monotone"
            dataKey="plannedValue"
            stroke={CHART_COLORS.primary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />

          {/* Earned Value - Secondary line */}
          <Line
            type="monotone"
            dataKey="earnedValue"
            stroke={CHART_COLORS.secondary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />

          {/* Actual Cost - Tertiary line */}
          <Line
            type="monotone"
            dataKey="actualCost"
            stroke={CHART_COLORS.tertiary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Compact version of the S-Curve chart for dashboard views.
 */
export function SCurveChartCompact({ data }: { data: EVMData }) {
  return (
    <SCurveChart
      data={data}
      height={200}
      showGrid={false}
      periodCount={8}
    />
  );
}
